'use client';

import {
  useCallback,
  startTransition,
  useContext,
  KeyboardEvent,
  useState,
  useEffect,
} from 'react';

import { RealEstateDict } from '@monoimin/intl';

import { useParamsUpdater } from '@monoimin/hooks';

import { Range } from '../Range';

import { SelectRange } from '../SelectRange';

import { createRangeFromFiltersStateValue } from '../../utils/filters/createRangeFromFiltersStateValue';

import { FiltersContext } from './FiltersProvider';

import type { RangeFilterVariants, RangeFormatter } from '../../types';

type RangeFilterProps = {
  fromField: string;
  toField: string;
  placeholder: string;
  min: number;
  max: number;
  step: number;
  valueFormater?: RangeFormatter;
  newPathname?: string;
  realEstateDict: RealEstateDict;
  variant?: RangeFilterVariants;
  hideSlider?: boolean;
  hideInput?: boolean;
  className?: string;
  fromPlaceholder: string;
  toPlaceholder: string;
};

export const RangeFilter = ({
  fromField,
  toField,
  placeholder,
  min,
  max,
  step,
  valueFormater,
  realEstateDict,
  newPathname,
  variant,
  hideSlider,
  hideInput,
  className,
  fromPlaceholder,
  toPlaceholder,
}: RangeFilterProps) => {
  const [filtersState] = useContext(FiltersContext);

  const { onChange: onParamsChange } = useParamsUpdater();

  const fromParam = filtersState?.[fromField];

  const toParam = filtersState?.[toField];

  const [range, setRange] = useState<Array<number | string>>(
    createRangeFromFiltersStateValue(fromParam, toParam)
  );

  const onFilterChange = useCallback(
    (value: Array<number | string> | undefined) => {
      if (!Array.isArray(value)) {
        return;
      }

      startTransition(() => {
        setRange(value);
      });
    },
    []
  );

  const onReset = useCallback(() => {
    startTransition(() => {
      setRange([min, max]);
    });

    const defaultValue = { [fromField]: null, [toField]: null };

    onParamsChange(defaultValue);
  }, [min, max, fromField, toField, onParamsChange]);

  const onMouseUp = () => {
    if (!Array.isArray(range)) {
      return;
    }

    const newParams: { [key: string]: number | null } = {};

    const fromValue = Number(range[0]);

    const toValue = Number(range[1]);

    newParams[fromField] = fromValue !== min ? fromValue : null;

    newParams[toField] = toValue !== max ? toValue : null;

    onParamsChange(newParams, newPathname);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.code === 'Enter') {
      onMouseUp();
    }
  };

  useEffect(() => {
    const changedRange = createRangeFromFiltersStateValue(fromParam, toParam);

    setRange(changedRange);
  }, [fromParam, toParam]);

  if (variant === 'select') {
    return (
      <SelectRange
        value={range}
        min={min}
        max={max}
        step={step}
        onChange={onFilterChange}
        onMouseUp={onMouseUp}
        onKeyDown={onKeyDown}
        onReset={onReset}
        hideSlider={hideSlider}
        hideInput={hideInput}
        placeholder={placeholder}
        fromPlaceholder={fromPlaceholder}
        toPlaceholder={toPlaceholder}
        realEstateDict={realEstateDict}
        valueFormatter={valueFormater}
      />
    );
  }

  return (
    <Range
      value={range}
      min={min}
      max={max}
      step={step}
      onChange={onFilterChange}
      onMouseUp={onMouseUp}
      onKeyDown={onKeyDown}
      hideSlider={hideSlider}
      hideInput={hideInput}
      classNames={{
        container: className,
      }}
      fromPlaceholder={fromPlaceholder}
      toPlaceholder={toPlaceholder}
    />
  );
};
